import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { SectionHeader } from "../../components/detailLayout";
import TabsCst from "@components/TabsCst";
import FiltersAccordionCst from "@components/FiltersAccordionCst";
import { FiltersBarValue } from "@components/FiltersBarCst/types";
import { PageFilterField } from "@hooks/usePageFilters";
import { PagesID } from "@utils/utilsConfigurations";
import {
  Folder,
  Folders,
  GalleryVerticalEnd,
  GraduationCap,
  ListFilter,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { ServicesURL, downloadBlobFile } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import PaginatorCst from "@components/PaginatorCst";
// import SortMenuCst from "@components/SortMenuCst";
import { SearchInput } from "src/UI/SearchInput";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { mapAggregatedType } from "../../../Catalog/types";
import UserLoEnrollmentRow from "./UserLoEnrollmentRow";
import EnrollmentHistoryModal from "../../../Catalog/UsersAndGroups/components/EnrollmentHistoryModal";
import EditEnrollmentModal, {
  buildConfigFromEnrollment,
  disabledByPriority,
} from "../../../Catalog/UsersAndGroups/components/EditEnrollmentModal";
import EnrollLOModal from "@pages/Contents/V2/Corporate/UsersGroups/components/EnrollLOModal";
import { EnrollType } from "../../../Catalog/Enrollment/types";

const PAGE_SIZE = 5;
const LO_TYPE_BY_TAB: Record<string, string> = {
  activities: "ACTIVITY",
  courses: "COURSE",
  paths: "PATH",
};
const ACTIVITY_CATEGORIES = [
  "DA_XAPI",
  "DA_SCORM",
  "DA_PHYSICAL_CLASS",
  "DA_VIRTUAL_CLASS",
  "DA_EXTERNAL_RES",
  "DA_YOUTUBE",
  "DA_LINKEDIN",
  "DA_SURVEY",
  "DA_URL_RES",
  "DA_MATERIAL",
  "DA_GOALS",
  "DA_MEETING",
  "DA_FINAL_BALANCE",
  "DA_LTI",
];
const COURSE_PATH_CATEGORIES = ["BLENDED", "ASYNC", "SYNC"];

export const LearningObjectSection = () => {
  const { t: tUserDetail } = useTranslation("user-detail-v2");
  const { t } = useTranslation("catalog-v2");
  const { t: tRoles } = useTranslation("input");
  const { t: tEnroll } = useTranslation("users-and-groups");

  const theme = useTheme();

  const { id: corporateId = "", userId = "" } = useParams();

  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{
    entity: any;
    scope: "single" | "group";
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<any>(null);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [editGroupTarget, setEditGroupTarget] = useState<any>(null);
  const [groupEnrollSource, setGroupEnrollSource] = useState<any>(null);

  const [loTab, setLoTab] = useState("all");
  const [loPage, setLoPage] = useState(1);
  const [loPageSize, setLoPageSize] = useState(PAGE_SIZE);
  const [loSearch, setLoSearch] = useState("");
  // const [loSort, setLoSort] = useState("id-desc");
  const [fieldFilters, setFieldFilters] = useState<FiltersBarValue>({});

  const filterExcludeKeys = useMemo(
    () => (loTab === "all" ? ["title"] : ["title", "type"]),
    [loTab],
  );

  const disabledFilterOptions = (
    field: PageFilterField,
    value: FiltersBarValue,
  ): string[] | undefined => {
    if (field.key === "category") {
      const effectiveType =
        loTab !== "all" ? LO_TYPE_BY_TAB[loTab] : (value.type as string);
      if (effectiveType === "COURSE" || effectiveType === "PATH") {
        return ACTIVITY_CATEGORIES;
      }
      if (effectiveType === "ACTIVITY") return COURSE_PATH_CATEGORIES;
      return undefined;
    }
    if (field.key === "type") {
      const category = value.category as string;
      if (COURSE_PATH_CATEGORIES.includes(category)) return ["ACTIVITY"];
      if (ACTIVITY_CATEGORIES.includes(category)) return ["COURSE", "PATH"];
    }
    return undefined;
  };

  const loUrl = userId
    ? ServicesURL.getLOBasedOnStudentId
        .replace(":idCorporate", corporateId || "1")
        .replace(":studentId", userId)
    : null;

  const loTypeQueryParams = useMemo(
    () =>
      loTab !== "all" && LO_TYPE_BY_TAB[loTab]
        ? { type: LO_TYPE_BY_TAB[loTab] }
        : undefined,
    [loTab],
  );

  const {
    items: paginatedLO,
    totalItems: loTotal,
    totalPages: loPages,
    isLoading: loadingLo,
    mutate,
  } = usePaginatedList(corporateId && userId ? loUrl : null, {
    page: loPage,
    pageSize: loPageSize,
    search: loSearch || undefined,
    fieldFilters: { ...fieldFilters, ...loTypeQueryParams },
  });

  const hasActiveFilters = useMemo(() => {
    const hasValue = (val: any): boolean => {
      if (val === undefined || val === null || val === "") return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") return Object.values(val).some(hasValue);
      return true;
    };
    return Object.values(fieldFilters || {}).some(hasValue);
  }, [fieldFilters]);

  const displayLoading = loadingLo;
  const displayTotalItems = loTotal;
  const displayTotalPages = loPages;
  const displayEmpty = paginatedLO.length === 0;

  const loTabs = [
    {
      key: "all",
      label: t("tabs.all"),
      icon: <GalleryVerticalEnd size={"0.75rem"} />,
    },
    {
      key: "activities",
      label: t("tabs.activities"),
      icon: <GraduationCap size={"0.75rem"} />,
    },
    {
      key: "courses",
      label: t("tabs.courses"),
      icon: <Folder size={"0.75rem"} />,
    },
    {
      key: "paths",
      label: t("tabs.paths"),
      icon: <Folders size={"0.75rem"} />,
    },
  ];

  // const sortOptions = [
  //   { label: t("sort.id-desc"), value: "id-desc" },
  //   { label: t("sort.most-recent"), value: "most-recent" },
  //   { label: t("sort.least-recent"), value: "least-recent" },
  //   { label: t("sort.alphabetical-az"), value: "alphabetical-az" },
  //   { label: t("sort.alphabetical-za"), value: "alphabetical-za" },
  //   { label: t("sort.duration-short"), value: "duration-short" },
  //   { label: t("sort.duration-long"), value: "duration-long" },
  // ];

  useEffect(() => {
    if (!displayLoading && loPage > displayTotalPages) {
      setLoPage(Math.max(displayTotalPages, 1));
    }
  }, [displayLoading, loPage, displayTotalPages]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { entity: target, scope } = deleteTarget;
    if (scope === "single" && !target.enrollId) return;
    if (
      scope === "group" &&
      (target.massiveGroupId == null || !target.groupType)
    )
      return;
    setIsDeleting(true);
    try {
      const url =
        scope === "group"
          ? `${ServicesURL.postEnrollmentMassive}?corporateId=${corporateId}&groupId=${target.massiveGroupId}&groupType=${target.groupType}&objectId=${target.id}`
          : `${ServicesURL.deleteEnrollment}?enrollmentEId=${target.enrollId}`;
      await fetcher(url, MethodType.DELETE);
      setDeleteTarget(null);
      mutate();
      openToast({
        severity: "success",
        message: String(tEnroll("card.delete-confirm.success")),
      });
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(tEnroll("card.delete-confirm.error")),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const downloadCertificate = async (target: any) => {
    if (target.certificateId == null || !userId) return;
    try {
      const url = `${ServicesURL.getIssuedCertificate.replace(
        ":certificateId",
        String(target.certificateId),
      )}?objectId=${target.id}&learnerId=${userId}`;
      await downloadBlobFile(
        url,
        null,
        `issued_certificate_lo_${target.id}_learner_${userId}.pdf`,
        MethodType.GET,
      );
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(tEnroll("card.certificate-error")),
      });
    }
  };

  const deleteTargetName = deleteTarget?.entity?.title ?? "";

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader
          sx={{ mb: 1 }}
          title={tUserDetail("lo.title")}
          subtitle={tUserDetail("lo.subtitle")}
        />

        <TabsCst
          tabs={loTabs}
          activeKey={loTab}
          onChange={(k) => {
            setLoTab(k);
            setLoPage(1);
            if (k !== "all") {
              setFieldFilters(({ type, ...rest }) => rest);
            }
          }}
        />
        <Stack
          flexDirection={"row"}
          gap={"1rem"}
          flexWrap={"wrap"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <SearchInput
            id="user-detail-lo-search"
            value={loSearch ?? ""}
            placeholder={tUserDetail("search")}
            onChange={(e: any) => {
              setLoSearch(e.target.value);
              setLoPage(1);
            }}
            sx={{
              width: { xs: "100%", sm: "15rem" },
            }}
          />
          <Stack
            flexDirection={"row"}
            gap={"1rem"}
            flexWrap={"wrap"}
            alignItems={"center"}
          >
            <ButtonSecondary
              startIcon={<ListFilter size={16} />}
              onClick={() => setFiltersPanelOpen((prev) => !prev)}
              sxButton={{
                height: "2.25rem",
                ...((hasActiveFilters || filtersPanelOpen) && {
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.action.hover,
                }),
              }}
            >
              {t("filters-button")}
            </ButtonSecondary>
            {/* <SortMenuCst
              id="catalog-sort"
              value={loSort}
              options={sortOptions}
              onChange={(next) => setLoSort(next || "id-desc")}
            /> */}
          </Stack>
        </Stack>

        <FiltersAccordionCst
          open={filtersPanelOpen}
          onClose={() => setFiltersPanelOpen(false)}
          pageId={PagesID["corporate.users.los.overall"]}
          value={fieldFilters}
          onApply={(next) => {
            setFieldFilters(next);
            setLoPage(1);
          }}
          excludeKeys={filterExcludeKeys}
          optionsToDisable={disabledFilterOptions}
        />

        {displayLoading ? (
          <CircularProgress />
        ) : displayEmpty ? (
          <Typography sx={{ color: theme.palette.text.tertiary }}>
            {t("lo.empty")}
          </Typography>
        ) : (
          paginatedLO.map((raw: any, index: number) => {
            const loId = raw.id ?? raw.activity_id ?? index;
            const objectId = raw.id ?? raw.activity_id;
            const entityType = mapAggregatedType(raw.type);
            const entity = {
              ...raw,
              id: loId,
              title: raw.title || raw.name || raw.activity_name,
              type: raw.category ?? raw.type,
              entityType,
              userId,
              enrollId: raw.enrollId ?? raw.enroll_id,
              enrollType: raw.enrollType ?? raw.enroll_type,
              massiveGroupId: raw.massiveGroupId ?? raw.massive_group_id,
              groupType: raw.groupType ?? raw.group_type,
              expirationDate: raw.expirationDate ?? raw.expiration_date,
              daysToComplete: raw.daysToComplete ?? raw.days_to_complete,
              isExpirationFixed:
                raw.isExpirationFixed ?? raw.is_expiration_fixed,
              isExpirationHard: raw.isExpirationHard ?? raw.is_expiration_hard,
              attributes: raw.attributes ?? raw.attributes_ids ?? [],
            };
            return (
              <UserLoEnrollmentRow
                key={loId}
                entity={entity}
                entityType={entityType}
                onEditEnrollment={
                  objectId ? () => setEditTarget(entity) : undefined
                }
                onDeleteEnrollment={
                  objectId
                    ? () => setDeleteTarget({ entity, scope: "single" })
                    : undefined
                }
                onEditGroupEnrollment={
                  objectId ? () => setEditGroupTarget(entity) : undefined
                }
                onDeleteGroupEnrollment={
                  objectId
                    ? () => setDeleteTarget({ entity, scope: "group" })
                    : undefined
                }
                onHistory={
                  objectId ? () => setHistoryTarget(entity) : undefined
                }
                onDownloadCertificate={
                  objectId ? () => downloadCertificate(entity) : undefined
                }
              />
            );
          })
        )}

        {displayTotalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PaginatorCst
              page={loPage}
              pageSize={loPageSize}
              totalItems={displayTotalItems}
              onPageChange={(next) => setLoPage(next)}
              onPageSizeChange={(next) => {
                setLoPageSize(next);
                setLoPage(1);
              }}
            />
          </Box>
        )}
      </Box>

      <ConfirmDialogCst
        open={!!deleteTarget}
        title={String(
          tEnroll(
            deleteTarget?.scope === "group"
              ? "card.delete-group-confirm.title"
              : "card.delete-confirm.title",
          ),
        )}
        description={String(
          tEnroll(
            deleteTarget?.scope === "group"
              ? "card.delete-group-confirm.description"
              : "card.delete-confirm.description",
            { name: deleteTargetName },
          ),
        )}
        confirmLabel={String(tEnroll("card.delete-confirm.confirm"))}
        cancelLabel={String(tEnroll("card.delete-confirm.cancel"))}
        confirmVariant="danger"
        isSubmitting={isDeleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <EnrollmentHistoryModal
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        objectId={historyTarget?.id ?? 0}
        objectTitle={historyTarget?.title}
        user={historyTarget}
      />

      <EditEnrollmentModal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        corporateId={corporateId}
        objectId={editTarget?.id ?? 0}
        objectType={editTarget?.entityType}
        objectTitle={editTarget?.title}
        user={editTarget}
        onSuccess={mutate}
      />

      <ConfirmDialogCst
        open={!!editGroupTarget}
        title={String(tEnroll("card.edit-group-confirm.title"))}
        description={String(
          tEnroll("card.edit-group-confirm.description", {
            groupId: editGroupTarget?.massiveGroupId ?? "-",
          }),
        )}
        confirmLabel={String(tEnroll("card.edit-group-confirm.confirm"))}
        cancelLabel={String(tEnroll("card.delete-confirm.cancel"))}
        onClose={() => setEditGroupTarget(null)}
        onConfirm={() => {
          setGroupEnrollSource(editGroupTarget);
          setEditGroupTarget(null);
        }}
      />

      <EnrollLOModal
        open={!!groupEnrollSource}
        onClose={() => setGroupEnrollSource(null)}
        subjectType="group"
        subject={
          groupEnrollSource
            ? {
                id: groupEnrollSource.massiveGroupId,
                name:
                  groupEnrollSource.groupName ||
                  `ID ${groupEnrollSource.massiveGroupId}`,
              }
            : null
        }
        groupType={groupEnrollSource?.groupType ?? "STATIC"}
        corporateId={String(corporateId)}
        onSuccess={mutate}
        initialStep="config"
        initialSelection={
          groupEnrollSource
            ? [
                {
                  id: groupEnrollSource.id,
                  kind: groupEnrollSource.entityType,
                  title: groupEnrollSource.title ?? "",
                  entity: {},
                },
              ]
            : []
        }
        initialConfig={
          groupEnrollSource
            ? buildConfigFromEnrollment(groupEnrollSource)
            : undefined
        }
        extraDisabledEnrollTypes={disabledByPriority(
          groupEnrollSource?.enrollType as EnrollType | undefined,
        )}
      />
    </>
  );
};
